import React, { useMemo } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper,
    TablePagination, Tooltip, Stack, Chip,
} from '@mui/material';
import ReactTablePdfDownload from 'src/shared/components/table/components/ReactTablePdfDownload';
import ReactTableXLSXDownload from 'src/shared/components/table/components/ReactTableXLSXDownload';

// ─── Columns with friendly labels matching `orders_failed` table ──────────────
const COLUMN_CONFIG = [
    { key: 'orderNo', label: 'Order No', sticky: true },
    { key: 'erpOrderNo', label: 'ERP Order No' },
    { key: 'salesOrderNo', label: 'Sales Order No' },
    { key: 'createdDateTime', label: 'Created Date' },
    { key: 'requestedDateTimeSlot', label: 'Requested Slot' },
    { key: 'name', label: 'Customer Name' },
    { key: 'contactNo', label: 'Contact No' },
    { key: 'address', label: 'Address' },
    { key: 'areaName', label: 'Area' },
    { key: 'emiratesName', label: 'Emirates' },
    { key: 'city', label: 'City' },
    { key: 'buildingName', label: 'Building' },
    { key: 'floorNo', label: 'Floor' },
    { key: 'doorNo', label: 'Door' },
    { key: 'landmark', label: 'Landmark' },
    { key: 'postalCode', label: 'Postal Code' },
    { key: 'orderZoneName', label: 'Zone' },
    { key: 'grlCode', label: 'GRL Code' },
    { key: 'shortCode', label: 'Short Code' },
    { key: 'orderAmount', label: 'Order Amount' },
    { key: 'orderSalesAmount', label: 'Sales Amount' },
    { key: 'orderTotalAmount', label: 'Total Amount' },
    { key: 'riderCollectionAmount', label: 'Collection Amount' },
    { key: 'paymentTypeId', label: 'Payment Type' },
    { key: 'sapOrderType', label: 'SAP Order Type' },
    { key: 'storeNo', label: 'Store No' },
    { key: 'reqtimeslot', label: 'Req. Time Slot' },
    { key: 'orderInstruction', label: 'Instruction' },
    { key: 'genericError', label: 'Error Reason', error: true },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (val) => {
    if (!val) return '';
    try { return new Date(val).toLocaleString(); } catch { return val; }
};

const AMOUNT_KEYS = ['orderAmount', 'orderSalesAmount', 'orderTotalAmount', 'riderCollectionAmount'];
const DATE_KEYS = ['createdDateTime', 'requestedDateTimeSlot'];

const formatCell = (col, val) => {
    if (val === null || val === undefined) return '—';
    if (DATE_KEYS.includes(col.key)) return formatDate(val);
    if (AMOUNT_KEYS.includes(col.key)) return Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2 });
    return String(val);
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderFailedReport = ({
    isReport = [],
    exportReport,
    page,
    rowsPerPage,
    totalElements,
    onPageChange,
    onRowsPerPageChange,
}) => {

    // Only show columns that have at least one non-null value in the dataset
    const visibleCols = useMemo(() => {
        if (!Array.isArray(isReport) || isReport.length === 0) return COLUMN_CONFIG;
        return COLUMN_CONFIG.filter(col =>
            isReport.some(row => row[col.key] !== null && row[col.key] !== undefined && row[col.key] !== '')
        );
    }, [isReport]);

    const exportData = useMemo(() => {
        const data = (exportReport && exportReport.length > 0) ? exportReport : (isReport || []);
        return data.map(row => {
            const obj = {};
            visibleCols.forEach(col => { obj[col.label] = formatCell(col, row[col.key]); });
            return obj;
        });
    }, [isReport, exportReport, visibleCols]);

    const handleChangePage = (_, newPage) => onPageChange(newPage);
    const handleChangeRowsPerPage = (e) => onRowsPerPageChange(parseInt(e.target.value, 10));

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <span style={{ fontSize: '1.5rem' }}>❌</span>
                    <Typography variant="h5" className="fw-bold" sx={{ color: '#dc2626', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Order Failed Report
                    </Typography>
                    <Chip
                        label={`${totalElements.toLocaleString()} records`}
                        size="small"
                        sx={{ background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: '0.75rem' }}
                    />
                </Box>
                <Stack direction="row" spacing={1}>
                    <ReactTableXLSXDownload
                        csvData={exportData}
                        fileName="Order_Failed_Report"
                        title="ORDER FAILED REPORT"
                    />
                    <ReactTablePdfDownload
                        csvData={exportData}
                        fileName="Order_Failed_Report"
                        title="ORDER FAILED REPORT"
                        orientation="landscape"
                    />
                </Stack>
            </Box>

            <TableContainer
                component={Paper}
                sx={{ maxHeight: 620, border: '1px solid #fecaca', borderRadius: 2 }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={thCell('#dc2626')} style={{ minWidth: 50 }}>S.No</TableCell>
                            {visibleCols.map(col => (
                                <TableCell
                                    key={col.key}
                                    sx={{
                                        ...thCell(col.error ? '#b91c1c' : '#dc2626'),
                                        minWidth: col.key === 'address' || col.key === 'genericError' ? 200 : 120,
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(isReport) && isReport.map((row, idx) => (
                            <TableRow
                                key={idx}
                                sx={{
                                    background: idx % 2 === 0 ? '#fff' : '#fff8f8',
                                    '&:hover': { background: '#fee2e2' },
                                }}
                            >
                                <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap', color: '#64748b' }}>
                                    {page * rowsPerPage + idx + 1}
                                </TableCell>
                                {visibleCols.map(col => {
                                    const raw = row?.[col.key];
                                    const display = formatCell(col, raw);
                                    const isErr = col.error && raw;
                                    return (
                                        <TableCell
                                            key={col.key}
                                            sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                                        >
                                            <Tooltip title={display} arrow placement="top">
                                                {isErr
                                                    ? <Chip label={display} size="small" sx={{ background: '#fee2e2', color: '#dc2626', fontWeight: 600, fontSize: '0.72rem', maxWidth: 220 }} />
                                                    : <span style={{ color: '#1e293b' }}>{display}</span>
                                                }
                                            </Tooltip>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100, 200]}
                component="div"
                count={totalElements}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

// ─── Table header cell style ──────────────────────────────────────────────────
const thCell = (bg) => ({
    backgroundColor: bg,
    color: '#fff',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    fontSize: '0.78rem',
});

export default OrderFailedReport;
